<template>
  <div v-if="pathway.data.title_image" :class="[isExpandedView ? 'expanded-tile' : 'pathway-tile']">
    <div
      v-if="!isExpandedView"
      class="empty-close-icon"
      @click="toggleExpandedView(pathway, true)"
      @keydown.enter="toggleExpandedView(pathway, true)"
    ></div>
    <div class="tile-header">
      <h1
        v-html="cms.asHtml(pathway.data.title)"
        class="pathway-title"
        :data-test="`pathway-title-${pathway.pathwayUid}`"
      ></h1>
      <button
        v-if="isExpandedView"
        class="btn-close-sm"
        @click="toggleExpandedView(pathway, false)"
        data-test="close-expanded"
        type="button"
      ></button>
    </div>
    <div class="pathway-tile-grid">
      <div class="description grid-item">
        <p class="pathway-desc" :data-test="`pathway-desc-${pathway.pathwayUid}`">
          {{ getDescription(isExpandedView) }}
        </p>
      </div>
      <div class="pathway-img-container grid-item">
        <img
          :src="pathway.data.title_image.tile.url"
          :alt="cms.asText(pathway.data.title)"
          class="pathway-tile-img"
          data-test="pathway-tile-img"
        />
      </div>
      <div v-if="isExpandedView" class="benefits-container">
        <h3>Benefits</h3>
        <ul class="benefits-list" :data-test="`pathway-benefits-${pathway.pathwayUid}`">
          <li v-for="(benefit, index) in benefits" :key="index" class="benefit-item">
            <img src="../../assets/star-icon.svg" alt="bullet-point" class="star-icon" />
            <p>{{ benefit }}</p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import cms from "../../services/cms/cms";
import { PathwayTileData } from "./select-team-pathway.vue";

const props = defineProps<{
  pathway: PathwayTileData;
  isExpandedView: boolean;
}>();

const emit = defineEmits(["toggle-expanded-view"]);

const benefits = ref<any[] | undefined>([]);

const getBenefitList = (): void => {
  if (props.pathway) {
    benefits.value = props.pathway.data.benefits?.map((benefit) => {
      return benefit.benefit_description[0].text;
    });
  }
};

const getDescription = (isExpandedView: boolean): string => {
  const { description } = props.pathway.data;
  if (description && isExpandedView) return description[0].text;
  return props.pathway.data.short_description ?? "";
};

const toggleExpandedView = (pathway: PathwayTileData, isOpen: boolean): void => {
  emit("toggle-expanded-view", { pathway, isOpen });
};

onMounted(() => {
  getBenefitList();
});
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables";

.empty-close-icon {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.pathway-tile,
.expanded-tile {
  border: 1px solid @light-gray;
  border-radius: 5px;
  padding: 0 2rem 1rem;

  @media (max-width: @screen-sm) {
    width: 100%;
  }
}

.pathway-tile-grid {
  display: grid;
  gap: 0.5rem;
}

.pathway-tile {
  max-width: 500px;
  height: 160px;
  position: relative;

  &:hover {
    cursor: pointer;
    box-shadow: 1px 1px 15px 5px rgba(238, 238, 238, 0.51);
  }

  .pathway-tile-grid {
    grid-template-columns: 60% auto;
    grid-template-rows: 100%;
    height: 70%;
    width: 100%;

    @media (max-width: @screen-sm-max) {
      height: unset;
    }
  }

  .description {
    grid-column: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .pathway-img-container {
    grid-column: 2;
    justify-self: center;
    align-self: center;
  }
}

.pathway-img-container {
  width: 90%;
  height: 90%;
  padding: 10px;
  display: flex;
  flex-direction: column; // Required to stop stretching in Safari
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    max-height: 100%;
  }
}

.expanded-tile {
  width: 95%;
  margin: 1rem auto;
  min-height: 40vh;
  box-shadow: 1px 1px 10px 5px rgba(238, 238, 238, 0.51);

  @media (max-width: @screen-sm) {
    max-height: fit-content;
    height: unset;
    min-height: unset;
  }

  .pathway-tile-grid {
    grid-template-columns: 60% auto;
    grid-template-rows: auto auto;
    height: 70%;
    width: 100%;

    @media (max-width: @screen-sm-max) {
      height: unset;
      grid-template-rows: auto 100px auto;
    }
  }

  .description {
    grid-column: 1 / span 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-bottom: 1px solid @light-gray;
  }

  .pathway-img-container {
    grid-column: 2;
    grid-row: 2;
    justify-self: center;
    align-self: center;
    height: 70%;
    width: 70%;

    @media (max-width: @screen-sm-max) {
      grid-column: 1 / span 2;
      height: 100%;
      width: 100%;
      margin-top: 10px;
    }
  }
}

.tile-header {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid @light-gray;
  width: 100%;
  margin-bottom: 0.2rem;
}

.benefits-container {
  grid-column: 1;
  grid-row: 2;
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  @media (max-width: @screen-sm-max) {
    grid-template-rows: auto auto;
    grid-row: 3;
    grid-column: 1 / span 2;
  }

  h3 {
    font-size: 22px;
    margin-top: 0;
  }
}

ul.benefits-list {
  padding-left: 0;
  margin: 0.5rem 0;
}

.benefits-list {
  list-style: none;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  color: @form-text-color;
}

.star-icon {
  margin-right: 1rem;
}

.btn-close-sm {
  margin-top: 1rem;
}
</style>

<style lang="less">
@import (reference) "../../styles/bootstrap/variables";

h1.pathway-title {
  margin: 0;
}

.pathway-title {
  color: @dark-background-color;
  h1 {
    font-size: 18px;
  }
}

.pathway-desc {
  padding-top: 1rem;
  color: @form-text-color;
  p {
    font-size: 16px;
  }
}
</style>
